import React, { useState } from 'react';
import { Modal, Button, Checkbox, Badge, Input } from '@/components/ui';
import { useZones } from '@/modules/maintenance/zones/hooks/useMaintenanceZones';
import { useAssignContractZones } from '../hooks/useMaintenanceContracts';
import { useToast } from '@/components/ui/Toast';

interface ContractZoneAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  contractId: string;
  projectId: string;
  companyId: string;
  alreadyAssignedZoneIds: string[];
}

export function ContractZoneAssignmentModal({
  isOpen,
  onClose,
  contractId,
  projectId,
  companyId,
  alreadyAssignedZoneIds
}: ContractZoneAssignmentModalProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const { toast } = useToast();

  const { zones, isLoading } = useZones(companyId, {
    projectId,
    pageSize: 100, // Get all project zones
    status: 'active'
  });

  const { mutateAsync: assignZones, isPending } = useAssignContractZones();

  // Filter out already assigned zones and apply search
  const availableZones = zones.filter(zone =>
    !alreadyAssignedZoneIds.includes(zone.id) &&
    (zone.zone_name.toLowerCase().includes(search.toLowerCase()) ||
      zone.zone_code.toLowerCase().includes(search.toLowerCase()))
  );

  const toggleZone = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleAssign = async () => {
    if (selectedIds.length === 0) return;
    try {
      await assignZones({ contractId, companyId, zoneIds: selectedIds });
      toast.success(`Đã gán ${selectedIds.length} khu vực vào hợp đồng`);
      setSelectedIds([]);
      onClose();
    } catch (error: any) {
      toast.error('Lỗi khi gán khu vực: ' + error.message);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Gán khu vực vào hợp đồng"
      size="lg"
    >
      <div className="space-y-4">
        <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 flex items-start gap-3">
          <i className="ri-information-line text-blue-500 mt-0.5" />
          <div className="text-xs text-blue-700 leading-relaxed">
            Chọn các khu vực từ dự án này để áp dụng gói bảo trì. Các khu vực đã có trong hợp đồng sẽ không hiển thị trong danh sách này.
          </div>
        </div>

        <Input
          placeholder="Tìm kiếm mã hoặc tên khu vực..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          icon="ri-search-line"
          className="mb-4"
        />

        <div className="max-h-[400px] overflow-y-auto border border-slate-100 rounded-xl divide-y divide-slate-50">
          {isLoading ? (
            <div className="p-8 text-center text-slate-400">Đang tải danh sách khu vực...</div>
          ) : availableZones.length === 0 ? (
            <div className="p-8 text-center text-slate-400">Không tìm thấy khu vực phù hợp</div>
          ) : (
            availableZones.map((zone) => (
              <div
                key={zone.id}
                className={`flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors cursor-pointer ${selectedIds.includes(zone.id) ? 'bg-blue-50/50' : ''}`}
                onClick={() => toggleZone(zone.id)}
              >
                <Checkbox
                  checked={selectedIds.includes(zone.id)}
                  onCheckedChange={() => toggleZone(zone.id)}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-slate-400 ">{zone.zone_code}</span>
                    <Badge variant="custom" className="text-[9px]  h-4 px-1">{zone.zone_type}</Badge>
                  </div>
                  <p className="font-bold text-slate-900 text-sm">{zone.zone_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-bold text-slate-700">{zone.area_m2?.toLocaleString() || 0} m²</p>
                  <p className="text-[10px] text-slate-400 ">Diện tích</p>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="flex justify-between items-center pt-4 border-t border-slate-100">
          <p className="text-sm text-slate-500">
            Đã chọn <span className="font-bold text-slate-900">{selectedIds.length}</span> khu vực
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Hủy</Button>
            <Button
              variant="primary"
              onClick={handleAssign}
              disabled={selectedIds.length === 0}
              isLoading={isPending}
            >
              Xác nhận gán
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
